import { makeStyles } from 'tss-react/mui';
import { Node, mergeAttributes, wrappingInputRule } from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';

export interface InlineInfoCardOptions {
  /**
   * HTML attributes to add to the inlinecard element
   * @default {}
   * @example { class: 'foo' }
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  HTMLAttributes: Record<string, any>;
}

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1),
    margin: theme.spacing(1, 0),
    backgroundColor: theme.palette.background.inlineInfoCard,
    borderRadius: 8,
    // `&&` raises specificity to beat the `& p` rule in commonEditorStyles.
    // Responsive clamp so the note scales with Dynamic Type like body text.
    '&& p': {
      fontSize: getResponsiveFontSize('0.75rem'),
      margin: 0,
      padding: '0 !important',
    },
  },
}));

const CustomNodeView = (): JSX.Element => {
  const { classes } = useStyles();

  return (
    <NodeViewWrapper as="div" className={classes.card}>
      <NodeViewContent as="p" />
    </NodeViewWrapper>
  );
};

/**
 * Matches a inlinecard to a `> ` as input.
 */
export const inputRegex = /^\s*>\s$/;

/**
 * This extension allows you to create inlinecards: content in a yellow box ideal for
 * notes. The content is rendered smaller than the surrounding text.
 */
export const InlineInfoCard = Node.create<InlineInfoCardOptions>({
  name: 'inlinecard',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  content: 'block+',

  group: 'block',

  defining: true,

  parseHTML() {
    return [
      {
        tag: 'div[data-tiptap-render="inline-info-card"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-tiptap-render': 'inline-info-card' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CustomNodeView);
  },
});
